/**
 * WorldClock — horizontal world clock + weather strip for the ROS header.
 *
 * Displays the user's local date/time on the left, a divider, then
 * 5 locations with live time (60s tick) and weather (30m refresh).
 * Weather fetched from Open-Meteo (free, no API key).
 *
 * Rendered inside the white top bar header (left side).
 *
 * Responsive:
 *   - >= 1024px: show all 5 cities
 *   - 640-1023px: show local + Houston + DC only, horizontally scrollable
 *   - < 640px: local time only — the other tiles just clipped hard against
 *     the header edge with no scroll affordance, reading as broken rather
 *     than intentionally scrollable, and city/weather tiles are low-priority
 *     info on a phone anyway.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ClockTile from "./ClockTile";

/* ── Location config ───────────────────────────────────────────────── */

const LOCATIONS = [
  { city: "Los Angeles", timezone: "America/Los_Angeles", tzLabel: "PT", lat: 34.0522, lon: -118.2437 },
  { city: "Denver", timezone: "America/Denver", tzLabel: "MT", lat: 39.7392, lon: -104.9903 },
  { city: "Houston", timezone: "America/Chicago", tzLabel: "CT", lat: 29.7604, lon: -95.3698, alwaysShow: true },
  { city: "Bogot\u00E1", timezone: "America/Bogota", tzLabel: "COT", lat: 4.7110, lon: -74.0721 },
  { city: "Washington, DC", timezone: "America/New_York", tzLabel: "ET", lat: 38.9072, lon: -77.0369, alwaysShow: true },
];

const WEATHER_REFRESH_MS = 30 * 60 * 1000; // 30 minutes

/* ── Component ─────────────────────────────────────────────────────── */

export default function WorldClock() {
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState({}); // keyed by city name
  const timerRef = useRef(null);
  const weatherTimerRef = useRef(null);

  // 60-second clock tick (no seconds displayed)
  useEffect(() => {
    // Align first tick to the top of the next minute for accuracy
    const msUntilNextMinute = (60 - new Date().getSeconds()) * 1000;
    const alignTimeout = setTimeout(() => {
      setNow(new Date());
      timerRef.current = setInterval(() => setNow(new Date()), 60000);
    }, msUntilNextMinute);
    return () => {
      clearTimeout(alignTimeout);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Fetch weather for all locations
  const fetchWeather = useCallback(async () => {
    const results = {};
    await Promise.allSettled(
      LOCATIONS.map(async (loc) => {
        try {
          const url = `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current_weather=true&temperature_unit=fahrenheit`;
          const res = await fetch(url);
          if (!res.ok) return;
          const data = await res.json();
          if (data.current_weather) {
            results[loc.city] = {
              temperature: data.current_weather.temperature,
              weathercode: data.current_weather.weathercode,
              windspeed: data.current_weather.windspeed,
            };
          }
        } catch {
          // fail silently — tile will show --°
        }
      })
    );
    setWeather((prev) => ({ ...prev, ...results }));
  }, []);

  // Initial fetch + 30-minute refresh
  useEffect(() => {
    fetchWeather();
    weatherTimerRef.current = setInterval(fetchWeather, WEATHER_REFRESH_MS);
    return () => clearInterval(weatherTimerRef.current);
  }, [fetchWeather]);

  // ── Local date/time formatting ─────────────────────────────────
  const localDate = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    }).format(now);
  }, [now]);

  const localTz = useMemo(() => {
    try {
      // Extract short timezone abbreviation (e.g., "CT", "ET", "PT")
      const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(now);
      const tzPart = parts.find((p) => p.type === "timeZoneName");
      return tzPart?.value || "";
    } catch {
      return "";
    }
  }, [now]);

  const localDayOfWeek = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(now);
  }, [now]);

  const localTimeStr = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(now);
  }, [now]);

  return (
    <div className="flex items-center overflow-x-auto scrollbar-none print:hidden pr-1">
      {/* ── Clock strip — single bounded pill widget ──────────────── */}
      <div className="flex items-stretch flex-shrink-0 rounded-xl border border-neutral-200 dark:border-white/[0.1] overflow-hidden bg-neutral-100/60 dark:bg-white/[0.05]">

        {/* Local time — anchor section, slightly heavier background */}
        <div
          className="flex items-center gap-2.5 px-3.5 py-2 bg-neutral-200/50 dark:bg-white/[0.07] sm:border-r border-neutral-200 dark:border-white/[0.1] flex-shrink-0"
          title={`Your local time (${localTz})`}
        >
          <span className="text-lg font-bold text-neutral-900 dark:text-white whitespace-nowrap tabular-nums leading-none tracking-tight">
            {localTimeStr}
          </span>
          <div className="flex flex-col items-start leading-none gap-0.5">
            <span className="text-[8px] font-bold text-neutral-400 dark:text-slate-400 uppercase tracking-widest">
              {localTz}
            </span>
            <span className="text-[10px] font-medium text-neutral-500 dark:text-slate-400 whitespace-nowrap">
              {localDayOfWeek} {localDate}
            </span>
          </div>
        </div>

        {/* World clock tiles */}
        {LOCATIONS.map((loc, i) => (
          <div
            key={loc.city}
            className={`flex items-center ${loc.alwaysShow ? "hidden sm:flex" : "hidden lg:flex"}`}
          >
            {i > 0 && (
              <div
                className={`w-px self-stretch bg-neutral-200 dark:bg-white/[0.08] flex-shrink-0 ${
                  loc.alwaysShow ? "hidden sm:block" : "hidden lg:block"
                }`}
              />
            )}
            <ClockTile
              city={loc.city}
              timezone={loc.timezone}
              tzLabel={loc.tzLabel}
              weather={weather[loc.city] || null}
              now={now}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
